import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import { Linkify } from '../common/Linkify';
import { apiGet } from '../../services/api/client';
import { useCustomers, useCreateCustomer } from '../../hooks/useProjects';
import { useLead, useProjectRoles, useCreateProjectRole, useAddLeadNote, useDeleteLeadNote, useDeleteLead, usePaymentTerms, useBillingTypes, useLeadLabels, useCreateLeadLabel, useLeadSources, useCreateLeadSource } from '../../hooks/useLeads';
import { useEscapeHandler } from '../../hooks/useEscapeHandler';
import { formatDisplayDateTime, parseLocaleNumber } from '../../utils/format';
import { parseDecimalInput } from '../../utils/parseDecimalInput';
import { labelTextColor, LABEL_COLORS } from '../../utils/labelColor';
import { LeadBillingPhasesEditor } from './LeadBillingPhasesEditor';
import { LeadContactsPicker } from './LeadContactsPicker';
import { LeadSlaPricingPicker } from './LeadSlaPricingPicker';
import LeadStageTrail from './LeadStageTrail';
import { LEAD_BILLING_FREQUENCIES, BILLING_FREQUENCY_LABELS } from '../../services/api/leads';
import type {
  Lead,
  CreateLeadInput,
  UpdateLeadInput,
  LeadRoleInput,
  LeadLabel,
  LeadContact,
  LeadSlaPricingConfiguration,
} from '../../services/api/leads';
import { DateField } from '../ui/DateField';

interface SalesUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

interface LeadFormModalProps {
  lead?: Lead | null;
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onSubmitCreate: (input: CreateLeadInput) => void;
  onSubmitUpdate: (id: number, input: UpdateLeadInput) => void;
}

const INITIAL_FORM = {
  name: '',
  customerId: '',
  contactPerson: '',
  contactEmail: '',
  contactPhone: '',
  dealValue: '',
  dealValueCurrency: 'CZK',
  paymentTermId: '',
  // MKS-94 — billing terms.
  billingTypeId: '',
  billingFrequency: '',
  contractUrl: '',
  sourceId: '',
  expectedCloseDate: '',
  expectedStartDate: '',
  expectedEndDate: '',
  ownerId: '',
};

export default function LeadFormModal({
  lead,
  isOpen,
  isLoading,
  onClose,
  onSubmitCreate,
  onSubmitUpdate,
}: LeadFormModalProps) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [roles, setRoles] = useState<LeadRoleInput[]>([]);
  const [labels, setLabels] = useState<LeadLabel[]>([]);
  const [contacts, setContacts] = useState<LeadContact[]>([]);
  const [slaPricingConfigurations, setSlaPricingConfigurations] = useState<
    LeadSlaPricingConfiguration[]
  >([]);
  const [customerFilter, setCustomerFilter] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [labelFilter, setLabelFilter] = useState('');
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLORS[0]!);
  // MKS-347 — the app's confirm dialog for "Delete Lead", replacing the
  // browser `confirm()` this used to call.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const isDirtyRef = useRef(false);
  const isEdit = !!lead;

  const { data: customers } = useCustomers();
  const createCustomerMutation = useCreateCustomer();
  const { data: projectRolesData } = useProjectRoles();
  const createRoleMutation = useCreateProjectRole();
  const { data: leadLabelsData } = useLeadLabels();
  const createLabelMutation = useCreateLeadLabel();
  const availableLabels = leadLabelsData?.data ?? [];
  const { data: paymentTermsData } = usePaymentTerms();
  const paymentTerms = paymentTermsData?.data ?? [];
  const { data: billingTypesData } = useBillingTypes();
  const billingTypes = billingTypesData?.data ?? [];
  const { data: leadSourcesData } = useLeadSources();
  const createSourceMutation = useCreateLeadSource();
  const leadSources = leadSourcesData?.data ?? [];
  const availableRoles = projectRolesData?.data ?? [];
  const { data: liveLead } = useLead(isOpen && lead ? lead.id : null);
  const liveNotes = liveLead?.data?.leadNotes ?? lead?.leadNotes ?? [];
  const addNoteMutation = useAddLeadNote();
  const deleteNoteMutation = useDeleteLeadNote();
  const deleteLeadMutation = useDeleteLead();
  const [newNote, setNewNote] = useState('');

  const { data: usersData } = useQuery({
    queryKey: ['salesUsers'],
    queryFn: () => apiGet<{ data: SalesUser[] }>('/leads/sales-users'),
    enabled: isOpen,
  });
  const salesUsers = usersData?.data ?? [];

  useEffect(() => {
    isDirtyRef.current = false;
    if (lead) {
      setRoles(lead.leadRoles.map((lr) => ({ roleId: lr.role.id, headcount: lr.headcount, hourlyRate: lr.hourlyRate, currency: lr.currency })));
      setLabels(lead.labels ?? []);
      setContacts(lead.contacts ?? []);
      setSlaPricingConfigurations(lead.slaPricingConfigurations ?? []);
      setForm({
        name: lead.name,
        customerId: String(lead.customer.id),
        contactPerson: lead.contactPerson ?? '',
        contactEmail: lead.contactEmail ?? '',
        contactPhone: lead.contactPhone ?? '',
        dealValue: lead.dealValue ? parseFloat(lead.dealValue).toString() : '',
        dealValueCurrency: lead.dealValueCurrency ?? 'CZK',
        paymentTermId: lead.paymentTermId ? String(lead.paymentTermId) : '',
        billingTypeId: lead.billingTypeId ? String(lead.billingTypeId) : '',
        billingFrequency: lead.billingFrequency ?? '',
        contractUrl: lead.contractUrl ?? '',
        sourceId: lead.sourceId ? String(lead.sourceId) : '',
        expectedCloseDate: lead.expectedCloseDate ?? '',
        expectedStartDate: lead.expectedStartDate ?? '',
        expectedEndDate: lead.expectedEndDate ?? '',
        ownerId: String(lead.owner.id),
      });
    } else {
      setForm(INITIAL_FORM);
      setRoles([]);
      setLabels([]);
      setContacts([]);
      setSlaPricingConfigurations([]);
    }
    setCustomerFilter('');
    setLabelFilter('');
    // Reset the in-progress note draft too. Without this, `newNote` (local
    // state) survives the modal's `if (!isOpen) return null` hide + reopen for
    // a different lead, so a draft typed on lead A reappears on lead B — the
    // "note shows up on all leads" report (2026-07-02). Only the DRAFT leaked;
    // saved notes are per-lead scoped end-to-end.
    setNewNote('');
  }, [lead, isOpen]);

  // Close customer dropdown on outside click
  useEffect(() => {
    if (!isCustomerDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target as Node)) {
        setIsCustomerDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCustomerDropdownOpen]);

  // Close label dropdown on outside click
  useEffect(() => {
    if (!isLabelDropdownOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target as Node)) {
        setIsLabelDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLabelDropdownOpen]);

  // Quick task 260924-nll: an unsaved note draft counts as unsaved state, just
  // like a dirty form field. The condition is derived from `newNote` rather than
  // set on `isDirtyRef` from the textarea's onChange, because the ref would stay
  // stuck dirty after the user clicks "Add" (which empties `newNote` but cannot
  // un-dirty the ref) and every later close would show a phantom prompt.
  const tryClose = () => {
    const hasNoteDraft = newNote.trim().length > 0;
    const isFormDirty = isDirtyRef.current;
    if (isFormDirty || hasNoteDraft) {
      let message = 'Discard changes?';
      if (isFormDirty && hasNoteDraft) {
        message = 'You have unsaved changes and an unsaved note. Discard both?';
      } else if (hasNoteDraft) {
        message = 'You have an unsaved note. Discard it?';
      }
      if (!window.confirm(message)) return;
    }
    onClose();
  };

  // Escape key handler with dirty-state guard (mirrors InviteExternalUserModal pattern)
  useEscapeHandler(isOpen, tryClose);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = form.name.trim();
    if (!trimmedName) return;
    const dealValue = form.dealValue ? parseDecimalInput(form.dealValue) : null;
    const paymentTermId = form.paymentTermId ? parseInt(form.paymentTermId, 10) : null;
    // MKS-94 — empty select/input clears the term. Sent as null rather than '',
    // which the backend body schema rejects (the frequency is an enum there).
    const billingTypeId = form.billingTypeId ? parseInt(form.billingTypeId, 10) : null;
    const billingFrequency = form.billingFrequency || null;
    const contractUrl = form.contractUrl.trim() || null;
    const sourceId = form.sourceId ? parseInt(form.sourceId, 10) : null;
    const customerId = parseInt(form.customerId, 10);
    if (!customerId) return;
    const pendingNote = newNote.trim();
    // MKS-344 — the picker's links, replacing the three free-text fields.
    const contactsPatch = {
      contacts: contacts.map((c) => ({ contactId: c.id, isPrimary: c.isPrimary })),
    };
    // MKS-346 — replace-all: the picker owns the lead's link set.
    const slaPricingPatch = {
      slaPricingConfigurationIds: slaPricingConfigurations.map((c) => c.id),
    };

    if (isEdit && lead) {
      // Quick task 260924-nll: the form save used to discard whatever was typed
      // into the Notes textarea — the small "Add" button was the only path that
      // ever posted it, so drafts were silently lost on "Save Changes".
      // Dispatched, never awaited: the note must not block, delay, or roll back
      // the lead update. Failures already surface through useAddLeadNote's own
      // onError toast ("Couldn't add note"). `newNote` is cleared only in this
      // call's success callback, mirroring the Add button, so a failed POST
      // does not blank the textarea.
      if (pendingNote) {
        addNoteMutation.mutate(
          { leadId: lead.id, content: pendingNote },
          { onSuccess: () => setNewNote('') }
        );
      }
      onSubmitUpdate(lead.id, {
        name: trimmedName,
        customerId,
        contactPerson: form.contactPerson || null,
        contactEmail: form.contactEmail || null,
        contactPhone: form.contactPhone || null,
        dealValue,
        dealValueCurrency: form.dealValueCurrency,
        paymentTermId,
        billingTypeId,
        billingFrequency,
        contractUrl,
        sourceId,
        expectedCloseDate: form.expectedCloseDate || null,
        expectedStartDate: form.expectedStartDate || null,
        expectedEndDate: form.expectedEndDate || null,
        ownerId: form.ownerId ? parseInt(form.ownerId, 10) : undefined,
        leadRoles: roles,
        labelIds: labels.map((l) => l.id),
        ...contactsPatch,
        ...slaPricingPatch,
      });
    } else {
      onSubmitCreate({
        name: trimmedName,
        customerId,
        contactPerson: form.contactPerson || null,
        contactEmail: form.contactEmail || null,
        contactPhone: form.contactPhone || null,
        dealValue,
        dealValueCurrency: form.dealValueCurrency,
        paymentTermId,
        billingTypeId,
        billingFrequency,
        contractUrl,
        sourceId,
        expectedCloseDate: form.expectedCloseDate || null,
        expectedStartDate: form.expectedStartDate || null,
        expectedEndDate: form.expectedEndDate || null,
        leadRoles: roles,
        labelIds: labels.map((l) => l.id),
        ...contactsPatch,
        ...slaPricingPatch,
      });
    }
  };

  const update = (field: string, value: string) => {
    isDirtyRef.current = true;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateRoles = (updater: (prev: LeadRoleInput[]) => LeadRoleInput[]) => {
    isDirtyRef.current = true;
    setRoles(updater);
  };

  const updateLabels = (updater: (prev: LeadLabel[]) => LeadLabel[]) => {
    isDirtyRef.current = true;
    setLabels(updater);
  };

  const filteredLabels = availableLabels.filter(
    (l) =>
      l.name.toLowerCase().includes(labelFilter.toLowerCase()) &&
      !labels.some((sel) => sel.id === l.id)
  );
  const isNewLabel =
    labelFilter.trim() !== '' &&
    !availableLabels.some((l) => l.name.toLowerCase() === labelFilter.trim().toLowerCase());

  const allCustomers = customers ?? [];
  const filtered = allCustomers.filter((c) =>
    c.name.toLowerCase().includes(customerFilter.toLowerCase())
  );
  const isNewCustomer = customerFilter.trim() !== '' &&
    !allCustomers.some((c) => c.name.toLowerCase() === customerFilter.trim().toLowerCase());

  const selectedCustomerName = allCustomers.find((c) => String(c.id) === form.customerId)?.name ?? '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Lead' : 'New Lead'}
          </h2>
          <button
            type="button"
            onClick={tryClose}
            aria-label="Close"
            className="-mr-1 -mt-1 p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto pr-1 -mr-1 flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1">Lead Name *</label>
              <input
                id="lead-name"
                type="text"
                required
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Customer combobox */}
            <div className="col-span-2" ref={customerDropdownRef}>
              <label htmlFor="lead-company" className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
              <div className="relative">
                <input
                  id="lead-company"
                  type="text"
                  value={isCustomerDropdownOpen ? customerFilter : selectedCustomerName}
                  onChange={(e) => {
                    setCustomerFilter(e.target.value);
                    if (!isCustomerDropdownOpen) setIsCustomerDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setCustomerFilter('');
                    setIsCustomerDropdownOpen(true);
                  }}
                  placeholder="Select or create a customer..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                >
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isCustomerDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isCustomerDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filtered.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          update('customerId', String(c.id));
                          setCustomerFilter('');
                          setIsCustomerDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                          String(c.id) === form.customerId ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        {c.name}
                      </button>
                    ))}
                    {isNewCustomer && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const newCustomer = await createCustomerMutation.mutateAsync(customerFilter.trim());
                            update('customerId', String(newCustomer.id));
                            setCustomerFilter('');
                            setIsCustomerDropdownOpen(false);
                          } catch {
                            // mutation error handled by React Query
                          }
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 text-green-700 border-t border-gray-100 flex items-center gap-2"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add &ldquo;{customerFilter.trim()}&rdquo;
                      </button>
                    )}
                    {filtered.length === 0 && !isNewCustomer && (
                      <p className="px-3 py-2 text-sm text-gray-400">No customers found</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {isEdit && salesUsers.length > 0 && (
              <div className="col-span-2">
                <label htmlFor="lead-owner" className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                <select
                  id="lead-owner"
                  value={form.ownerId}
                  onChange={(e) => update('ownerId', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {salesUsers.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {[u.lastName, u.firstName].filter(Boolean).join(' ') || u.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* MKS-344 — the reusable contacts picker, in place of the three
                free-text fields it replaced. */}
            <LeadContactsPicker
              value={contacts}
              onChange={(next) => {
                isDirtyRef.current = true;
                setContacts(next);
              }}
              customerId={form.customerId ? parseInt(form.customerId, 10) : null}
            />
            <div>
              <label htmlFor="lead-deal-value" className="block text-sm font-medium text-gray-700 mb-1">Deal Value</label>
              <div className="flex gap-1">
                <input
                  id="lead-deal-value"
                  type="number"
                  min="0"
                  step="any"
                  value={form.dealValue}
                  onChange={(e) => update('dealValue', e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <select
                  aria-label="Deal value currency"
                  value={form.dealValueCurrency}
                  onChange={(e) => update('dealValueCurrency', e.target.value)}
                  className="shrink-0 rounded-lg border border-gray-300 px-1 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="CZK">CZK</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="CHF">CHF</option>
                </select>
              </div>
            </div>
            {/* MKS-94 — Billing type. An INDEPENDENT field from Payment Terms
                below: this is what we bill (T&M / Fixed Price / retainer), that
                is when the invoice falls due. The catalogue endpoint already
                excludes the legacy `Default` template. */}
            <div>
              <label htmlFor="lead-billing-type" className="block text-sm font-medium text-gray-700 mb-1">Billing type</label>
              <select
                id="lead-billing-type"
                value={form.billingTypeId}
                onChange={(e) => update('billingTypeId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">—</option>
                {billingTypes.map((bt) => (
                  <option key={bt.id} value={String(bt.id)}>{bt.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lead-payment-terms" className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
              <select
                id="lead-payment-terms"
                value={form.paymentTermId}
                onChange={(e) => update('paymentTermId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">—</option>
                {paymentTerms.map((pt) => (
                  <option key={pt.id} value={String(pt.id)}>{pt.name} ({pt.days} days)</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lead-billing-frequency" className="block text-sm font-medium text-gray-700 mb-1">Billing frequency</label>
              <select
                id="lead-billing-frequency"
                value={form.billingFrequency}
                onChange={(e) => update('billingFrequency', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">—</option>
                {LEAD_BILLING_FREQUENCIES.map((f) => (
                  <option key={f} value={f}>{BILLING_FREQUENCY_LABELS[f]}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lead-contract-url" className="block text-sm font-medium text-gray-700 mb-1">Contract URL</label>
              <input
                id="lead-contract-url"
                type="url"
                inputMode="url"
                placeholder="https://…"
                value={form.contractUrl}
                onChange={(e) => update('contractUrl', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Link to the signed contract. http:// or https:// only.</p>
            </div>
            {/* MKS-136 — the phases this deal is billed in, which become the
                project's billing periods on conversion. Shown on an existing
                lead only (a draft needs a lead to hang off) and when the
                frequency says the deal is billed per phase. Renders nothing
                while the release flag is off. */}
            {isEdit && lead && form.billingFrequency === 'per_phase' && (
              <div className="sm:col-span-2">
                <LeadBillingPhasesEditor
                  leadId={lead.id}
                  currency={form.dealValueCurrency || 'CZK'}
                />
              </div>
            )}
            {/* MKS-346 — the SLA pricing configurations priced for this deal,
                next to the rest of the commercial terms. Doubles as the
                "lead modal lists the linked configurations" surface the ticket
                asks for: each row names the configuration, its monthly price
                and currency, and links to the configurator. */}
            <LeadSlaPricingPicker
              value={slaPricingConfigurations}
              onChange={(next) => {
                isDirtyRef.current = true;
                setSlaPricingConfigurations(next);
              }}
            />
            <div>
              <label htmlFor="lead-source" className="block text-sm font-medium text-gray-700 mb-1">Source</label>
              <div className="flex gap-1">
                <select
                  id="lead-source"
                  value={form.sourceId}
                  onChange={(e) => update('sourceId', e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">—</option>
                  {leadSources.map((s) => (
                    <option key={s.id} value={String(s.id)}>{s.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  title="Create new source"
                  onClick={async () => {
                    const name = window.prompt('New source name:');
                    if (!name?.trim()) return;
                    try {
                      const result = await createSourceMutation.mutateAsync(name.trim());
                      update('sourceId', String(result.data.id));
                    } catch {
                      // handled by React Query
                    }
                  }}
                  className="shrink-0 rounded-lg border border-gray-300 px-2 py-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="lead-close-date" className="block text-sm font-medium text-gray-700 mb-1">Expected Close Date</label>
              <DateField
                id="lead-close-date"
                value={form.expectedCloseDate}
                onChange={(nextDate) => update('expectedCloseDate', nextDate)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="lead-start-date" className="block text-sm font-medium text-gray-700 mb-1">Expected Start Date</label>
              <DateField
                id="lead-start-date"
                value={form.expectedStartDate}
                onChange={(nextDate) => update('expectedStartDate', nextDate)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="lead-end-date" className="block text-sm font-medium text-gray-700 mb-1">Expected End Date</label>
              <DateField
                id="lead-end-date"
                value={form.expectedEndDate}
                onChange={(nextDate) => update('expectedEndDate', nextDate)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            {/* Required Roles */}
            <div className="col-span-2" role="group" aria-labelledby="lead-required-roles-label">
              <label id="lead-required-roles-label" htmlFor="lead-add-role" className="block text-sm font-medium text-gray-700 mb-2">Required Roles</label>
              <div className="space-y-2">
                {roles.map((r, idx) => {
                  const ar = availableRoles.find((ar) => ar.id === r.roleId);
                  const roleName = ar ? (ar.code ? `${ar.code} — ${ar.name}` : ar.name) : '?';
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="flex-1 text-sm text-gray-700">{roleName}</span>
                      <input
                        type="number"
                        min={0.01}
                        max={99.99}
                        step={0.01}
                        value={r.headcount}
                        onChange={(e) => {
                          const updated = [...roles];
                          updated[idx] = { ...r, headcount: parseLocaleNumber(e.target.value) };
                          updateRoles(() => updated);
                        }}
                        className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                        title="Value between 0.01 and 99.99 (e.g. 0.5, 1.5, 2.25)"
                      />
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={r.hourlyRate ?? ''}
                        onChange={(e) => {
                          const updated = [...roles];
                          updated[idx] = { ...r, hourlyRate: e.target.value ? parseFloat(e.target.value) : null };
                          updateRoles(() => updated);
                        }}
                        placeholder="Rate"
                        className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500"
                        title="Hourly rate"
                      />
                      <select
                        value={r.currency ?? 'EUR'}
                        onChange={(e) => {
                          const updated = [...roles];
                          updated[idx] = { ...r, currency: e.target.value };
                          updateRoles(() => updated);
                        }}
                        className="w-16 rounded-lg border border-gray-300 px-1 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                        title="Currency"
                      >
                        <option value="EUR">EUR</option>
                        <option value="CZK">CZK</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                        <option value="CHF">CHF</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => updateRoles((prev) => prev.filter((_, i) => i !== idx))}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <select
                  id="lead-add-role"
                  value=""
                  onChange={(e) => {
                    const roleId = parseInt(e.target.value, 10);
                    if (!roleId || roles.some((r) => r.roleId === roleId)) return;
                    updateRoles((prev) => [...prev, { roleId, headcount: 1 }]);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Add a role...</option>
                  {availableRoles
                    .filter((ar) => !roles.some((r) => r.roleId === ar.id))
                    .map((ar) => (
                      <option key={ar.id} value={ar.id}>{ar.code ? `${ar.code} — ${ar.name}` : ar.name}</option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={async () => {
                    const name = window.prompt('New role name:');
                    if (!name?.trim()) return;
                    const code = window.prompt('Role code (e.g. Dev, SA, CE):') ?? undefined;
                    try {
                      const result = await createRoleMutation.mutateAsync({ name: name.trim(), code: code?.trim() || undefined });
                      updateRoles((prev) => [...prev, { roleId: result.data.id, headcount: 1 }]);
                    } catch {
                      // handled by React Query
                    }
                  }}
                  className="shrink-0 rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                  title="Create new role"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Labels */}
            <div className="col-span-2" ref={labelDropdownRef}>
              <label htmlFor="lead-add-label" className="block text-sm font-medium text-gray-700 mb-2">Labels</label>
              {labels.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {labels.map((l) => (
                    <span
                      key={l.id}
                      style={{ backgroundColor: l.color, color: labelTextColor(l.color) }}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                    >
                      {l.name}
                      <button
                        type="button"
                        onClick={() => updateLabels((prev) => prev.filter((x) => x.id !== l.id))}
                        className="hover:opacity-70"
                        aria-label={`Remove ${l.name}`}
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <input
                  id="lead-add-label"
                  type="text"
                  value={labelFilter}
                  onChange={(e) => {
                    setLabelFilter(e.target.value);
                    if (!isLabelDropdownOpen) setIsLabelDropdownOpen(true);
                  }}
                  onFocus={() => setIsLabelDropdownOpen(true)}
                  placeholder="Add or create a label..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {isLabelDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredLabels.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => {
                          updateLabels((prev) => [...prev, l]);
                          setLabelFilter('');
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                      >
                        <span style={{ backgroundColor: l.color }} className="inline-block w-3 h-3 rounded-full shrink-0" />
                        <span className="text-gray-700">{l.name}</span>
                      </button>
                    ))}
                    {isNewLabel && (
                      <div className="border-t border-gray-100 p-2">
                        <div className="flex items-center gap-1.5 mb-2">
                          {LABEL_COLORS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setNewLabelColor(c)}
                              style={{ backgroundColor: c }}
                              className={`w-5 h-5 rounded-full transition-transform ${newLabelColor === c ? 'ring-2 ring-offset-1 ring-gray-500 scale-110' : ''}`}
                              aria-label={`Color ${c}`}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          disabled={createLabelMutation.isPending}
                          onClick={async () => {
                            try {
                              const result = await createLabelMutation.mutateAsync({ name: labelFilter.trim(), color: newLabelColor! });
                              updateLabels((prev) => [...prev, result.data]);
                              setLabelFilter('');
                            } catch {
                              // handled by React Query
                            }
                          }}
                          className="w-full px-3 py-1.5 text-left text-sm hover:bg-green-50 text-green-700 rounded flex items-center gap-2 disabled:opacity-50"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Create &ldquo;{labelFilter.trim()}&rdquo;
                        </button>
                      </div>
                    )}
                    {filteredLabels.length === 0 && !isNewLabel && (
                      <p className="px-3 py-2 text-sm text-gray-400">
                        {availableLabels.length === 0 ? 'No labels yet — type to create one' : 'No matching labels'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* MKS-347 — why the deal was lost, shown on the lead itself (the
                board card carries the short version). Read off the data, not
                a flag: nothing can write a reason while the flag is off. */}
            {isEdit && lead?.stageKind === 'lost' && lead.lostReason && (
              <div className="col-span-2 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-800">
                  Lost: {lead.lostReason.name}
                </p>
                {lead.lostReasonNote && (
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-red-700">
                    <Linkify text={lead.lostReasonNote} />
                  </p>
                )}
              </div>
            )}

            {/* Every stage change, who made it and when. */}
            {isEdit && lead && <LeadStageTrail leadId={lead.id} />}

            {/* Notes timeline */}
            <div className="col-span-2">
              <label htmlFor="lead-new-note" className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              {isEdit && lead && (
                <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                  {liveNotes.length === 0 && (
                    <p className="text-xs text-gray-400">No notes yet.</p>
                  )}
                  {liveNotes.map((note) => (
                    <div key={note.id} className="flex gap-2 text-sm border-l-2 border-gray-200 pl-3 py-1">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-700 whitespace-pre-wrap break-words">
                          <Linkify text={note.content} />
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {[note.author.lastName, note.author.firstName].filter(Boolean).join(' ')} &middot;{' '}
                          {formatDisplayDateTime(note.createdAt)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteNoteMutation.mutate({ leadId: lead.id, noteId: note.id })}
                        className="shrink-0 p-0.5 text-gray-300 hover:text-red-500"
                        title="Delete note"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  id="lead-new-note"
                  rows={2}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder={isEdit ? 'Add a note...' : 'Add a note after creating the lead'}
                  disabled={!isEdit}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
                />
                {isEdit && lead && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!newNote.trim()}
                    isLoading={addNoteMutation.isPending}
                    onClick={() => {
                      if (!newNote.trim()) return;
                      addNoteMutation.mutate({ leadId: lead.id, content: newNote.trim() }, {
                        onSuccess: () => setNewNote(''),
                      });
                    }}
                  >
                    Add
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between pt-2 border-t border-gray-100">
            {isEdit ? (
              <Button
                variant="danger"
                type="button"
                isLoading={deleteLeadMutation.isPending}
                // MKS-347 — the app's own dialog, not the browser's `confirm()`:
                // a native confirm is unstyled, unlabelled and (in Chrome)
                // suppressible with "prevent this page from creating more
                // dialogs", which turns a destructive action into a silent one.
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete Lead
              </Button>
            ) : <div />}
            <div className="flex gap-3">
              {/*
                Quick task 260924-nll: Cancel goes through `tryClose`, not raw
                `onClose`. It was the only close control that bypassed every
                guard, so the most-clicked close path discarded note drafts
                silently. This also means Cancel now honours the pre-existing
                form-dirty "Discard changes?" confirmation, matching the X
                button and Escape.
              */}
              <Button variant="secondary" type="button" onClick={tryClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isLoading} disabled={!form.customerId}>
                {isEdit ? 'Save Changes' : 'Create Lead'}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* MKS-347 — the app's confirm dialog, replacing the browser's. Rendered
          outside the <form> so its buttons can never submit the lead. */}
      {lead && (
        <ConfirmModal
          isOpen={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          onConfirm={() =>
            deleteLeadMutation.mutate(lead.id, {
              onSuccess: () => {
                setConfirmDeleteOpen(false);
                onClose();
              },
            })
          }
          title="Delete lead"
          message={`Delete “${lead.name}”? This cannot be undone.`}
          confirmText="Delete lead"
          variant="danger"
          isLoading={deleteLeadMutation.isPending}
        />
      )}
    </div>
  );
}
